/**
 * 3-row preview mini-grid for one inbound-relation section.
 *
 * Slice 1: includes the new leftmost icon column (24px, sticky, no header),
 * one `ChevronRight` per row. Clicking the icon falls back to "open in new
 * TE tab" — Slice 3 rewires this to drill onto the navigation tree.
 *
 * Note (Two Results Grids parity): this mini-grid is intentionally **not** a
 * derivative of either production grid. It's a 3-row read-only preview with
 * a single navigation affordance.
 */

import { ChevronRight } from 'lucide-react';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { InboundRelation } from '../types/singleRowTypes';
import type { DatabaseEngineId } from '../../../../core/engines';
import { openTableExplorerTab } from '../../table-explorer/utils/openTableExplorerTab';
import { buildInboundForeignKeyFilters } from '../../table-explorer/utils/inboundForeignKeyUtils';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../../../components/ui/context-menu';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { openInNewExplorer, openInQueryEditor } from '../../common/services/openDrillTargetAction';

type RelatedDataMiniGridProps = {
  readonly relation: InboundRelation;
  readonly rows: ReadonlyArray<Record<string, unknown>>;
  readonly columns: ReadonlyArray<TableExplorerColumn>;
  readonly connectionId: string;
  readonly database: string;
  readonly engine?: DatabaseEngineId;
  readonly parentRow: Record<string, unknown>;
  /**
   * Slice 3 (EP6) — drill-icon click or context-menu "Open record".
   * `via` discriminates the two so the analytics layer can fire
   * `source: 'related-row-icon'` vs `'related-row-context'` per the spec
   * source-value glossary.
   */
  readonly onRowOpen?: (args: {
    row: Record<string, unknown>;
    via: 'icon' | 'context-menu';
    modifierKey: boolean;
  }) => void;
};

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '∅';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function RelatedDataMiniGrid({
  relation,
  rows,
  columns,
  connectionId,
  database,
  engine = 'mssql',
  parentRow,
  onRowOpen,
}: RelatedDataMiniGridProps) {
  const { track } = useAnalytics();

  // Slice 3 (EP6) — icon click. ⌘/Ctrl-click bypasses the drill and opens a
  // new TE tab on this row.
  const handleIconClick = (row: Record<string, unknown>, modifierKey: boolean) => {
    if (modifierKey) {
      // ⌘-click → open new explorer on the related table (preserves today's UX).
      const build = buildInboundForeignKeyFilters({
        foreignKey: relation.source,
        parentRow,
        looseRel: undefined,
      });
      const title = `${relation.fromSchema ? `${relation.fromSchema}.` : ''}${relation.fromTable} [FK]`;
      openTableExplorerTab({
        connectionId,
        database,
        schema: relation.fromSchema,
        tableName: relation.fromTable,
        title,
        filters: build.filters ?? [],
      });
      track('feature:fk_nav_open_new_explorer', {
        source: 'modifier-click-mini-grid',
        targetKind: 'row',
        engine,
      });
      return;
    }
    if (onRowOpen) {
      onRowOpen({ row, via: 'icon', modifierKey: false });
      return;
    }
    // Fallback (no chip-row support, e.g. EC1): open in new TE tab.
    const build = buildInboundForeignKeyFilters({
      foreignKey: relation.source,
      parentRow,
      looseRel: undefined,
    });
    if (!build.filters) return;
    const title = `${relation.fromSchema ? `${relation.fromSchema}.` : ''}${relation.fromTable} [FK]`;
    openTableExplorerTab({
      connectionId,
      database,
      schema: relation.fromSchema,
      tableName: relation.fromTable,
      title,
      filters: build.filters,
    });
  };

  // Slice 3 (EP6) — right-click → "Open record". Differs from icon click ONLY
  // by `via` so the parent fires `source: 'related-row-context'`.
  const handleContextOpen = (row: Record<string, unknown>) => {
    if (onRowOpen) {
      onRowOpen({ row, via: 'context-menu', modifierKey: false });
      return;
    }
    // Fallback (no chip-row support): mirror icon-click fallback.
    handleIconClick(row, false);
  };

  // EP6 secondary actions reused by the right-click context menu.
  const handleOpenInNewExplorerRow = (row: Record<string, unknown>) => {
    // Use row PK if known, otherwise fall back to the inbound filter view.
    void row;
    const build = buildInboundForeignKeyFilters({
      foreignKey: relation.source,
      parentRow,
      looseRel: undefined,
    });
    openTableExplorerTab({
      connectionId,
      database,
      schema: relation.fromSchema,
      tableName: relation.fromTable,
      title: `${relation.fromSchema ? `${relation.fromSchema}.` : ''}${relation.fromTable} [FK]`,
      filters: build.filters ?? [],
    });
    track('feature:fk_nav_open_new_explorer', {
      source: 'related-row-menu',
      targetKind: 'row',
      engine,
    });
  };

  const handleOpenInQueryEditorRow = (row: Record<string, unknown>) => {
    // Concern 2 — build the row-PK from the relation's targetPkColumns (the
    // referencing table's full PK). When the backend didn't surface PK
    // metadata (targetPkColumns is empty), fall back to the legacy
    // first-column heuristic so behavior matches pre-Concern-2 for
    // un-enriched FKs.
    const pkColumns =
      relation.targetPkColumns.length > 0
        ? relation.targetPkColumns
        : Object.keys(row).slice(0, 1);
    const pk = pkColumns.map((column) => ({ column, value: row[column] }));
    openInQueryEditor({
      connectionId,
      database,
      engineId: engine,
      target: { kind: 'row', schema: relation.fromSchema, table: relation.fromTable, pk },
    });
    track('feature:fk_nav_open_query_editor', {
      source: 'related-row-menu',
      targetKind: 'row',
      engine,
    });
  };

  // Keep `openInNewExplorer` import in scope even when the row-based version
  // above is used (TS strips unused imports otherwise).
  void openInNewExplorer;

  if (rows.length === 0) {
    return (
      <div
        className="text-xs text-muted-foreground py-2 px-3"
        data-testid={`mini-grid-empty-${relation.relationKey}`}
      >
        No related rows.
      </div>
    );
  }

  return (
    <div
      className="overflow-x-auto"
      data-testid={`mini-grid-${relation.relationKey}`}
    >
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr>
            <th
              className="sticky left-0 z-10 bg-muted/30 px-1 py-1 text-left"
              style={{ width: 24 }}
              aria-label="Drill into row"
            />
            {columns.slice(0, 6).map((col) => (
              <th
                key={col.name}
                className="px-2 py-1 text-left font-medium text-muted-foreground"
              >
                {col.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 3).map((row, idx) => (
            <ContextMenu key={idx}>
              <ContextMenuTrigger asChild>
                <tr
                  className="border-t border-border hover:bg-muted/20"
                  data-testid={`mini-grid-row-${relation.relationKey}-${idx}`}
                >
              <td
                className="sticky left-0 z-10 bg-background/95 px-1 py-1 border-r border-border/40"
                style={{ width: 24 }}
              >
                <button
                  type="button"
                  className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                  onClick={(e) => handleIconClick(row, e.metaKey || e.ctrlKey)}
                  data-testid="mini-grid-drill-icon"
                  aria-label="Open record"
                  title="Open record (⌘-click for new tab)"
                >
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </td>
              {columns.slice(0, 6).map((col) => (
                <td
                  key={col.name}
                  className="px-2 py-1 truncate max-w-[160px] text-foreground"
                  title={formatCell(row[col.name])}
                >
                  {formatCell(row[col.name])}
                </td>
              ))}
                </tr>
              </ContextMenuTrigger>
              <ContextMenuContent>
                {onRowOpen && (
                  <ContextMenuItem
                    className="text-xs"
                    onClick={() => handleContextOpen(row)}
                    data-testid="mini-grid-row-context-open-record"
                  >
                    <ChevronRight className="h-3.5 w-3.5 mr-2" />
                    Open record
                  </ContextMenuItem>
                )}
                <ContextMenuItem
                  className="text-xs"
                  onClick={() => handleOpenInNewExplorerRow(row)}
                  data-testid="mini-grid-row-context-open-in-new-explorer"
                >
                  Open in new explorer
                </ContextMenuItem>
                <ContextMenuItem
                  className="text-xs"
                  onClick={() => handleOpenInQueryEditorRow(row)}
                  data-testid="mini-grid-row-context-open-in-query-editor"
                >
                  Open in query editor
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          ))}
        </tbody>
      </table>
    </div>
  );
}
